import React, { useState, useEffect, useRef } from "react";
import { gsap } from "gsap";
import { FaFacebook, FaLinkedin } from "react-icons/fa";
import { AiFillInstagram } from "react-icons/ai";
import { HiMenuAlt1 } from "react-icons/hi";
import { BsThreadsFill } from "react-icons/bs";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";

const Sidebar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const iconsRef = useRef(null); // Use useRef to access icons DOM

  const handleMenuToggle = () => {
    setMenuOpen(!menuOpen);
  };

  useGSAP(() => {
    // Register GSAP and ScrollTrigger plugins
    gsap.registerPlugin(ScrollTrigger);

    const sidebar = document.getElementById("sidebar-menu");
    gsap.from(sidebar, { opacity: 0, duration: 1 });

    const icons = iconsRef.current?.querySelectorAll(".icon"); // Use iconsRef to access the DOM

    const isDarkColor = (color) => {
      const rgb = color.match(/\d+/g); // Extract numbers from rgb or rgba string
      const brightness = rgb
        ? (0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2]) / 255
        : 0; // Calculate brightness using luminance formula
      return brightness < 0.5; // If brightness is below 0.5, it's considered dark
    };

    // Function to update icon colors based on sidebar background color
    const updateIconColors = () => {
      const sidebarBgColor = window.getComputedStyle(sidebar).backgroundColor;
      const darkMode = isDarkColor(sidebarBgColor);

      icons.forEach((icon) => {
        if (darkMode) {
          gsap.to(icon, { color: "white" }); // Light icons if background is dark
        } else {
          gsap.to(icon, { color: "black" }); // Dark icons if background is light
        }
      });
    };

    updateIconColors(); // Set initial icon colors on component mount

    // Optionally, you can listen for changes in the sidebar's background color if it changes dynamically.
    window.addEventListener("resize", updateIconColors); // Recheck when the window resizes

    return () => {
      window.removeEventListener("resize", updateIconColors);
    };
  }, []); // Empty dependency array means this runs once when the component mounts

  return (
    <>
      {/* Sidebar */}
      <div
        id="sidebar"
        className="fixed left-0 top-0 h-full w-24 flex flex-col justify-between items-center bg-[#1d3557]/10 border-r border-r-[#bbbbbb4d] p-4 z-40 transition-colors duration-300"
      >
        <a
          id="menu-button"
          className=" text-white text-2xl hover:bg-[#457b9d] p-2 rounded-full cursor-pointer"
          onClick={handleMenuToggle}
        >
          <HiMenuAlt1 className="size-7 icon" />
        </a>
        <div className="logo icon">
          <img src="/Assets/sc-logo.jpg" className="w-16 rounded-full" alt="" />
        </div>
        <div
          className="social-icons flex flex-col justify-center items-center space-y-4"
          ref={iconsRef} // Attach the ref to the container holding the icons
        >
          <a
            href="https://www.facebook.com/socraticaliconsultants"
            target="_blank"
            rel="noopener noreferrer"
            className="icon text-white"
          >
            <FaFacebook className="size-5 text-white" />
          </a>
          <a
            href="https://www.instagram.com/socratic.ali.consultants?igsh=c2JjZXdubGQxdWVs"
            target="_blank"
            rel="noopener noreferrer"
            className="icon text-white"
          >
            <AiFillInstagram className="size-6 text-white" />
          </a>
          <a
            href="https://www.threads.net/@socratic.ali.consultants"
            target="_blank"
            rel="noopener noreferrer"
            className="icon text-white"
          >
            <BsThreadsFill className="size-5 text-white" />
          </a>
          <a
            href="https://www.linkedin.com/company/socraticali-consultants/ "
            target="_blank"
            rel="noopener noreferrer"
            className="icon text-white"
          >
            <FaLinkedin className="size-5 text-white" />
          </a>
        </div>
      </div>

      {/* Sidebar Menu */}
      <div
        id="sidebar-menu"
        className={`fixed left-0 top-0 h-full w-96 bg-[#cac8bb] p-6 ${
          menuOpen ? "" : "-translate-x-full"
        } transition-transform z-50 shadow-lg`}
      >
        <button
          id="close-menu"
          className="text-black text-lg absolute top-10 left-10 bg-[#457b9d] w-12 h-12 flex items-center justify-center rounded-full shadow-lg"
          onClick={handleMenuToggle}
        >
          X
        </button>
        <nav className="mt-[150px] ml-12">
          <ul className="space-y-4 text-black text-[20px] font-bold">
            <li>
              <a href="#home" className="hover:text-white transition duration-300">
                Home
              </a>
            </li>
            <li>
              <a href="#about" className="hover:text-white transition duration-300">
                About
              </a>
            </li>
            <li>
              <a href="#contact-us" className="hover:text-white transition duration-300">
                Contact
              </a>
            </li>
          </ul>
        </nav>

        <hr className="mt-24 border-t border-gray-300 w-[250px] mx-auto" />
        <nav className="mt-20 ml-12">
          {/* Latest Offer Button */}
          <a
            href="#calculator"
            className="bg-[#457b9d] w-[75%] text-black px-6 py-2 flex items-center rounded-md font-normal text-[16px]"
          >
            CALCULATE FEE
            <svg
              className="w-4 h-4 ml-2"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 5l7 7-7 7"
              ></path>
            </svg>
          </a>

          {/* Social Icons */}
          <div className="flex space-x-4 mt-8">
            <a
              href="https://www.facebook.com/socraticaliconsultants"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-500 hover:text-black"
            >
              <i className="fab fa-facebook-f text-xl"></i>
            </a>
            <a
              href="https://www.instagram.com/socratic.ali.consultants?igsh=c2JjZXdubGQxdWVs"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-500 hover:text-black"
            >
              <i className="fab fa-instagram text-xl"></i>
            </a>
            <a
              href="https://www.threads.net/@socratic.ali.consultants"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-500 hover:text-black"
            >
              <BsThreadsFill className="size-5 mt-1" />
            </a>
            <a
              href="https://www.linkedin.com/company/socraticali-consultants/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-500 hover:text-black"
            >
              <i className="fab fa-linkedin-in text-xl"></i>
            </a>
          </div>

          {/* Logo */}
          <div className="mt-8">
            <img src="/Assets/new-logo.png" alt="Logo" className="w-10" />
          </div>
        </nav>
      </div>
    </>
  );
};

export default Sidebar;
